<template>
  <ServiceDialog
    :isModal="isModal"
    :servicesData="servicesData"
    @select="selectedDepartment"
    @close="close"
  />
  <div class="flex gap-4 flex-col lg:flex-row border border-primary p-4 rounded-md shadow-sm">
    <div class="w-full lg:w-1/3">
      <div class="relative w-full flex items-center gap-x-4">
        <h4 class="md:text-xl text-primary font-semibold">{{ $t('searchFor') }}</h4>
        <tippy
          :arrow="false"
          :offset="[0, 0]"
          interactive
          trigger="click"
          placement="bottom-start"
          theme="light-border"
          class="w-full flex-1"
          @state="(state) => (tippyState = state)"
        >
          <button
            :class="tippyState.isShown ? 'border-primary' : 'border-grey-300'"
            class="flex-1 w-full py-1 px-4 ps-3 border rounded-sm text-sm"
          >
            <div class="w-full flex justify-between items-center">
              <span v-if="selectedCategory" class="w-full text-start line-clamp-1">
                {{ $t(selectedCategory.name) }}
              </span>
              <span v-else class="w-full text-start text-gray-500">
                {{ $t('pleaseSelect') }}
              </span>
              <div class="flex gap-x-2 items-center">
                <button
                  v-if="selectedCategory"
                  class="hover:scale-125"
                  @click.stop="clearSelection"
                >
                  <FontAwesomeIcon :icon="faTimes" class="text-primary pt-1.5" />
                </button>
                <FontAwesomeIcon :icon="faCaretDown" class="text-primary" />
              </div>
            </div>
          </button>
          <template #content="{ hide }">
            <div class="w-full flex flex-col text-start gap-y-1">
              <button
                v-for="(item, key) in authStore.availableCategoriesPerApplicationId"
                :key="key"
                class="w-full px-3 py-1.5 text-sm text-start text-primary hover:text-primary-700 hover:bg-gray-200"
                @click="
                  () => {
                    hide()
                    selectedCategory = item
                  }
                "
              >
                {{ $t(item.name) }}
              </button>
            </div>
          </template>
        </tippy>
      </div>
    </div>
    <div v-if="selectedCategory && !isLoading" class="flex-1 flex flex-col gap-4">
      <component
        :is="searchContent[selectedCategory.id]"
        :searchCategoryId="selectedCategory.id"
        :key="selectedCategory.id"
        :services="services"
        :page-length="6"
        title-class="line-clamp-3 break-words text-primary text-xs max-w-max lg:max-w-[72ch]"
        class="w-full h-full"
      >
        <template #rowActions="{ service }">
          <button
            class="w-fit ps-1 rounded-md bg-primary/80 hover:bg-primary flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
            @click="onStartService(service)"
          >
            <span class="ps-2 pe-1 ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
              {{ $t('serviceStart') }}
            </span>
            <div class="flex justify-center items-center h-full bg-black/10 p-1.5 rounded-e">
              <FontAwesomeIcon
                :icon="faArrowCircleRight"
                class="w-4 fill-primary/80 hover:fill-primary text-white"
              />
            </div>
          </button>

          <span
            v-tippy="{ content: $t(service.serviceFullDescription) }"
            class="size-4 flex justify-center items-center rounded-full bg-primary"
          >
            <faInfo class="size-2 fill-white" />
          </span>
        </template>
      </component>
    </div>
  </div>
</template>

<script setup>
import ServiceDirectory from '@/components/search/ServiceDirectory.vue'
import ServiceDialog from '@/components/search/Services/ServiceDialog.vue'
import axiosInstance from '@/services'
import { useAuthStore } from '@/stores/auth'
import { useLookupsStore } from '@/stores/lookups'
import SearchMyRequests from '@/views/individual/dashboard/SearchPanel/SearchMyRequests.vue'
import SearchSponsoredAndFamily from '@/views/individual/dashboard/SearchPanel/SearchSponsoredAndFamily.vue'
import { faArrowCircleRight, faCaretDown, faTimes } from '@fortawesome/free-solid-svg-icons'
import { onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import faInfo from '@/assets/icons/faInfo.svg?component'

const authStore = useAuthStore()
const lookupsStore = useLookupsStore()
const router = useRouter()

const tippyState = ref()
const selectedCategory = ref()
const services = ref([])
const isLoading = ref(true)
const selectedService = ref()
const isModal = ref(false)
const servicesData = ref()

const searchContent = {
  1: ServiceDirectory,
  2: SearchSponsoredAndFamily,
  3: SearchMyRequests,
  4: SearchSponsoredAndFamily,
  5: SearchSponsoredAndFamily
}

const clearSelection = () => {
  selectedCategory.value = null
}

watch(selectedCategory, async (category) => {
  if (category.id == 1) {
    isLoading.value = true
    await fetchServices()
    isLoading.value = false
    return
  }

  isLoading.value = false
})

async function fetchServices() {
  const nameDetails = await lookupsStore.getServiceNameDetails()

  const params = {
    administrativeRegionId: 1
  }
  const modules = await axiosInstance.get('/api/application/service/transactions', { params })

  const serviceList = []

  modules.forEach((module) => {
    module.moduleServices.forEach((parentService) => {
      parentService.childs.forEach((subService) => {
        subService.serviceTransactions.forEach((transaction) => {
          const serviceNameAsList = [
            module.localizedName,
            parentService.localizedName,
            subService.localizedName,
            transaction.localizedName
          ]

          serviceList.push({
            transactionId: transaction.id,
            hint: serviceNameAsList.join('-')
          })
        })
      })
    })
  })

  const serviceIds = serviceList.map((s) => s.transactionId)

  services.value = nameDetails
    .filter((nd) => serviceIds.includes(nd.serviceTransId))
    .map((service) => {
      return {
        ...service,
        hint: serviceList.find((sl) => sl.transactionId == service.serviceTransId)?.hint,
        localizedServiceName: service.transactionsGroup
          ? service.transactionsGroup + ' - ' + service.serviceShortDescription
          : service.transactionDescription + ' - ' + service.serviceDescription
      }
    })
}

const onStartService = async (service) => {
  if (!service) return
  selectedService.value = service
  const {
    transactionId: serviceTransactionId,
    serviceTransactionId: transactionId,
    lookupAdminRegionId: adminRegionId,
    moduleId
  } = service

  const payload = {
    serviceTransactionId: service.serviceTransId
  }

  const data = await axiosInstance.get('/api/service/transactions/config', { params: payload })
  const destinationQuery = {
    transactionId,
    moduleId,
    adminRegionId
  }
  if ('requiredEmployee' in data && data.requiredEmployee) {
    router.push({ name: 'IndSponsoredRequests', query: destinationQuery })
  }

  if ('requiredFamilyBook' in data && data.requiredFamilyBook) {
    router.push({ name: 'IndFamilyMembers', query: destinationQuery })
  }

  isModal.value = true
}

const close = () => {
  isModal.value = false
}

const selectedDepartment = () => {
  router.push({
    name: 'ServiceForm',
    params: {
      serviceId: selectedService.value.serviceTransId
    }
  })
}

const getDepartmentData = async () => {
  try {
    const response = await axiosInstance.get('/api/individual/info')
    response.sponsorFileDepartments.forEach((item) => {
      servicesData.value = item
    })
  } catch (error) {
    console.error('err:', error)
  }
}

onMounted(() => {
  getDepartmentData()
  selectedCategory.value = authStore.availableCategoriesPerApplicationId.find((c) => c.id == 3)
})
</script>

<style lang="scss" scoped>
:deep(.tippy-box) {
  // background: tomato;
  box-shadow: theme('boxShadow.md');
}
</style>
